import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Skeleton,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search,
  TrendingUp,
  TrendingDown,
  Refresh as RefreshIcon,
  PlaylistAdd,
  SmartToy,
} from '@mui/icons-material';
import axios from 'axios';
import { formatCurrency, formatPercent, formatVolume } from '../utils/formatters';
import MobileStockCard from '../components/shared/MobileStockCard';

const Research = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [stockQuote, setStockQuote] = useState(null);
  const [movers, setMovers] = useState([]);
  const [losers, setLosers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [error, setError] = useState('');
  const [addingSymbol, setAddingSymbol] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const handleAddToWatchlist = async (e, symbol, name) => {
    e.stopPropagation();
    try {
      setAddingSymbol(symbol);
      const response = await axios.post('/watchlist/add', {
        symbol: symbol,
        company_name: name || symbol
      });
      if (response.data.success) {
        setSnackbar({ open: true, message: `${symbol} added to watchlist` });
      } else {
        setSnackbar({ open: true, message: response.data.message || 'Failed to add' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to add to watchlist' });
    } finally {
      setAddingSymbol(null);
    }
  };

  useEffect(() => {
    fetchMarketData();
  }, []);

  const fetchMarketData = async () => {
    try {
      setLoading(true);
      setError('');

      const [moversRes, losersRes] = await Promise.allSettled([
        axios.get('/stocks/movers?count=12'),
        axios.get('/stocks/losers?count=12')
      ]);

      if (moversRes.status === 'fulfilled') setMovers(moversRes.value.data);
      if (losersRes.status === 'fulfilled') setLosers(losersRes.value.data);
    } catch (err) {
      setError('Unable to fetch market data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    var symbolPattern = /^[A-Za-z]{1,5}$/;
    if (!symbolPattern.test(searchQuery.trim())) {
      setError('Please enter a valid stock symbol (1-5 letters)');
      return;
    }
    try {
      setSearchLoading(true);
      setError('');
      setStockQuote(null);
      var symbol = searchQuery.trim().toUpperCase();
      var response = await axios.get('/stocks/quote/' + symbol);
      setStockQuote(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Symbol not found');
    } finally {
      setSearchLoading(false);
    }
  };

  var renderMoversTable = function(title, data, isGainers) {
    return (
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={1}
          sx={{
            p: 1.5, mb: 0,
            backgroundColor: isGainers ? 'success.main' : 'error.main',
            color: 'white',
            borderRadius: isMobile ? 2 : '4px 4px 0 0',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            {isGainers ? <TrendingUp /> : <TrendingDown />}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{title}</Typography>
          </Box>
          <Chip
            label={data.length + ' stocks'}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'inherit', fontWeight: 'bold' }}
          />
        </Paper>

        {/* Mobile: Card layout */}
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {data.length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>No data available</Typography>
            ) : data.map(function(stock) {
              return (
                <MobileStockCard
                  key={stock.symbol}
                  symbol={stock.symbol}
                  name={stock.name || stock.symbol}
                  currentPrice={stock.price}
                  gainLoss={stock.change}
                  gainLossPercent={stock.changePercent}
                  onClick={function() { navigate('/stock/' + stock.symbol); }}
                  actions={
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={function(e) { handleAddToWatchlist(e, stock.symbol, stock.name); }}
                      disabled={addingSymbol === stock.symbol}
                    >
                      {addingSymbol === stock.symbol ? <CircularProgress size={16} /> : <PlaylistAdd />}
                    </IconButton>
                  }
                />
              );
            })}
          </Box>
        ) : (
        /* Desktop: Table layout */
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: '0 0 4px 4px' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell><strong>Symbol</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell align="right"><strong>Price</strong></TableCell>
                <TableCell align="right"><strong>Change</strong></TableCell>
                <TableCell align="right"><strong>% Change</strong></TableCell>
                <TableCell align="right"><strong>Volume</strong></TableCell>
                <TableCell align="center"><strong>Watchlist</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(function(stock) {
                return (
                  <TableRow
                    key={stock.symbol}
                    hover
                    onClick={function() { navigate('/stock/' + stock.symbol); }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {stock.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{stock.name || stock.symbol}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(stock.price)}</TableCell>
                    <TableCell align="right">
                      <Typography color={stock.change >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(stock.change)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatPercent(stock.changePercent)}
                        color={stock.changePercent >= 0 ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{formatVolume(stock.volume)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Add to Watchlist">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={function(e) { handleAddToWatchlist(e, stock.symbol, stock.name); }}
                          disabled={addingSymbol === stock.symbol}
                        >
                          {addingSymbol === stock.symbol ? <CircularProgress size={18} /> : <PlaylistAdd />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary">No data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">Research</Typography>
        </Box>
        <Tooltip title="Refresh market data">
          <IconButton onClick={fetchMarketData} disabled={loading} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="flex-start">
            <TextField
              fullWidth
              label="Search stock symbol"
              value={searchQuery}
              onChange={function(e) { setSearchQuery(e.target.value.toUpperCase()); }}
              onKeyPress={function(e) { if (e.key === 'Enter') handleSearch(); }}
              placeholder="e.g., AAPL, JPM, MSFT"
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searchLoading || !searchQuery.trim()}
              startIcon={searchLoading ? <CircularProgress size={16} /> : <Search />}
              sx={{ minWidth: 100 }}
            >
              Search
            </Button>
          </Box>

          {stockQuote && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {stockQuote.symbol}
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(stockQuote.price)}
                </Typography>
                <Chip
                  icon={stockQuote.change >= 0 ? <TrendingUp /> : <TrendingDown />}
                  label={formatCurrency(stockQuote.change) + ' (' + formatPercent(stockQuote.changePercent) + ')'}
                  color={stockQuote.change >= 0 ? 'success' : 'error'}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {stockQuote.name}
              </Typography>
              <Box display="flex" gap={3} flexWrap="wrap">
                <Typography variant="body2">Open: {formatCurrency(stockQuote.open)}</Typography>
                <Typography variant="body2">High: {formatCurrency(stockQuote.high)}</Typography>
                <Typography variant="body2">Low: {formatCurrency(stockQuote.low)}</Typography>
                <Typography variant="body2">Volume: {formatVolume(stockQuote.volume)}</Typography>
              </Box>
              <Box mt={2} display="flex" gap={1}>
                <Button size="small" variant="contained" onClick={function() { navigate('/stock/' + stockQuote.symbol); }}>
                  View Details
                </Button>
                <Button size="small" variant="outlined" onClick={function() { navigate('/stock/' + stockQuote.symbol); }}>
                  Trade
                </Button>
                <Tooltip title="Ask AI Assistant about this stock">
                  <Button size="small" variant="outlined" startIcon={<SmartToy />} onClick={function() { navigate('/assistant'); }}>
                    Ask Assistant
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={function() { setError(''); }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box>
          <Skeleton variant="rounded" height={280} sx={{ mb: 3, borderRadius: 2 }} />
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 2 }} />
        </Box>
      ) : (
        <Box>
          {renderMoversTable('Top Gainers', movers, true)}
          {renderMoversTable('Top Losers', losers, false)}
        </Box>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={function() { setSnackbar({ open: false, message: '' }); }}
        message={snackbar.message}
      />
    </Container>
  );
};

export default Research;
